import { useState } from "react";
import { Logo } from "@/components/Logo";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Building2, User, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Seo } from "@/components/Seo";

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input {...props} className="w-full rounded-xl border border-slate-700 bg-[#0d1420] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none" />
    </div>
  );
}

export default function AuthPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login, register, magicVerify } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [magic, setMagic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", company_name: "", mfa_code: "", magic_token: "" });
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const go = async () => {
    setBusy(true);
    try {
      if (mode === "signup") {
        await register({ email: f.email, password: f.password, name: f.name, company_name: f.company_name });
        toast.success("Workspace created");
        nav("/dispatch");
      } else {
        const r = await login(f.email, f.password, f.mfa_code || undefined);
        if (r?.mfa_required) { setMfa(true); toast.info("Enter your MFA code"); return; }
        toast.success("Signed in");
        nav("/home");
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const requestMagic = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/magic/request", { email: f.email });
      if (data.dev_token) { setF((s) => ({ ...s, magic_token: data.dev_token })); toast.success("Link ready (dev token filled)"); }
      else toast.success("If the email exists, a link was sent");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyMagic = async () => {
    setBusy(true);
    try { await magicVerify(f.magic_token); toast.success("Signed in"); nav("/home"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen bg-[#0B0F17] lg:grid-cols-2">
      <Seo title="Sign in | SiteControlHQ" description="Sign in to your SiteControlHQ workspace or create a new one." path="/login" noindex />
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://images.pexels.com/photos/21812143/pexels-photo-21812143.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Technician at panel" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="eyebrow text-amber-400">Field ops command</div>
          <h2 className="mt-3 font-display text-3xl font-bold text-white">One integrated release. Truck to office.</h2>
          <p className="mt-3 max-w-md text-slate-300">Offline-first capture, legally durable signatures, and AI that always passes review.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm rise">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</Link>
          <div className="flex items-center gap-2.5">
            <Logo />
          </div>
          <h1 className="mt-8 font-display text-2xl font-bold text-white">
            {magic ? "Magic link sign-in" : mode === "signup" ? "Create your workspace" : "Welcome back"}
          </h1>

          {!magic ? (
            <div className="mt-6 space-y-3">
              {mode === "signup" && <>
                <Field icon={Building2} placeholder="Company name" value={f.company_name} onChange={on("company_name")} data-testid="signup-company-input" />
                <Field icon={User} placeholder="Your name" value={f.name} onChange={on("name")} data-testid="signup-name-input" />
              </>}
              <Field icon={Mail} type="email" placeholder="Email" value={f.email} onChange={on("email")} data-testid="auth-email-input" />
              <Field icon={Lock} type="password" placeholder="Password" value={f.password} onChange={on("password")} data-testid="auth-password-input" />
              {mfa && <Field icon={KeyRound} placeholder="6-digit MFA code" value={f.mfa_code} onChange={on("mfa_code")} data-testid="auth-mfa-input" />}
              <button data-testid="auth-submit-btn" disabled={busy} onClick={go} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-60">
                {busy ? "Working…" : mode === "signup" ? "Create workspace" : "Sign in"}
              </button>
              <button data-testid="magic-toggle-btn" onClick={() => setMagic(true)} className="w-full text-center text-xs text-cyan-400 hover:text-cyan-300">Use a magic link instead</button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <Field icon={Mail} type="email" placeholder="Email" value={f.email} onChange={on("email")} data-testid="magic-email-input" />
              <button data-testid="magic-request-btn" disabled={busy} onClick={requestMagic} className="w-full rounded-xl border border-slate-700 py-3 text-sm text-white hover:border-slate-500">Send link</button>
              {f.magic_token && <>
                <Field icon={KeyRound} placeholder="Link token" value={f.magic_token} onChange={on("magic_token")} data-testid="magic-token-input" />
                <button data-testid="magic-verify-btn" disabled={busy} onClick={verifyMagic} className="btn-amber w-full rounded-xl py-3 text-sm">Verify & sign in</button>
              </>}
              <button onClick={() => setMagic(false)} className="w-full text-center text-xs text-slate-400 hover:text-white">Back to password</button>
            </div>
          )}

          {!magic && (
            <p className="mt-6 text-center text-sm text-slate-400">
              {mode === "signup" ? "Already have an account? " : "New to SiteControl? "}
              <button data-testid="auth-mode-toggle" onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="font-semibold text-amber-400 hover:text-amber-300">
                {mode === "signup" ? "Sign in" : "Start free"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
